<template>
  <div v-if="inline" class="c-date-range-filter--inline w-full flex flex-col bg-base" :v-bind="$attrs">
    <span class="flex items-center px-6 font-size-4">
      <span>{{ name }}</span>
      <span v-if="modelValue?.length" class="ml-2 cursor-pointer underline font-size-3" @click="onClearSelection">
        {{ t('canvasUI.components.filters.clear') }}
      </span>
    </span>
    <div class="mb-2 py-2 px-6">
      <div v-for="option in options" :key="option.value" class="flex gap-2 h-8">
        <NCheckbox
          :checked="isArrayEqual(modelValue, translatePredefinedRange(option.value))"
          class="px-2"
          :disabled="disabled"
          :onUpdate:checked="(v) => onOptionSelect(option, v)"
        >
          {{ option.label }}
        </NCheckbox>
      </div>
      <NDatePicker
        v-if="allowCustomDateRange"
        v-model:show="isDatePickerOpen"
        :default-formatted-value="modelValue"
        :disabled="disabled"
        :formatted-value="modelValue"
        :on-update:value="onCustomRangeUpdate"
        panel
        size="small"
        type="daterange"
        :value-format="METADATA_FILTER_DATE_FORMAT"
      />
    </div>
  </div>
  <NPopover
    v-else
    class="c-date-range-filter"
    :on-clickoutside="onOutsideClick"
    placement="bottom-start"
    raw
    :show="isOpen"
    :show-arrow="false"
    trigger="manual"
  >
    <template #trigger>
      <NTag
        class="select-none cursor-pointer"
        :style="tagStyle"
        :themeOverrides="{
          borderRadius: '4px',
        }"
        @click="togglePopover"
      >
        <span v-if="!activeFilter && !modelValue">{{ filterName }}</span>
        <span v-else class="font-bold">{{ `${filterName}:` }}</span>
        <span v-if="activeFilter || modelValue" class="ml-1">
          {{ activeFilter ? activeFilter.label : `${modelValue?.[0]} - ${modelValue?.[1]}` }}
        </span>
      </NTag>
    </template>
    <div class="c-date-range-filter__popover w-[350px] rounded mt-2 bg-base" :v-bind="$attrs">
      <div class="pa-2">
        <div v-for="option in options" :key="option.value" class="flex gap-2 h-8">
          <NCheckbox
            :checked="isArrayEqual(modelValue, translatePredefinedRange(option.value))"
            class="px-2"
            :disabled="disabled"
            :onUpdate:checked="(v) => onOptionSelect(option, v)"
          >
            {{ option.label }}
          </NCheckbox>
        </div>
        <NDatePicker
          v-if="allowCustomDateRange"
          v-model:show="isDatePickerOpen"
          closeOnSelect
          :default-formatted-value="modelValue"
          :disabled="disabled"
          :formatted-value="modelValue"
          :on-update:value="onCustomRangeUpdate"
          size="small"
          type="daterange"
          :value-format="METADATA_FILTER_DATE_FORMAT"
        />
      </div>
      <div class="pa-2" :style="{ borderTop: `1px solid ${themeVars.primary5}` }">
        <CButton block class="pa-2" :disabled="disabled" text @click="onClearSelection">
          {{ t('canvasUI.components.filters.clearSelection') }}
        </CButton>
      </div>
    </div>
  </NPopover>
</template>

<script lang="ts" setup>
import { ref, computed } from 'vue'
import { NCheckbox, NDatePicker, NTag, NPopover } from 'naive-ui'
import { useI18n } from 'vue-i18n'
import { subYears, subDays, format } from 'date-fns'

import CButton from '@lib/components/CButton/CButton.vue'
import useThemeVars from '@lib/composables/useThemeVars'
import { METADATA_FILTER_DATE_FORMAT } from './filter.const'

type Option = {
  label: string
  value: 'custom_range' | 'last_week' | 'last_month' | 'last_year'
}

type ModelValue = [string, string] | null

type Props = {
  disabled?: boolean
  inline?: boolean
  name?: string
  modelValue: ModelValue
  options: Option[]
  allowCustomDateRange?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  allowCustomDateRange: false,
  disabled: false,
  inline: false,
  name: '',
  modelValue: null,
  options: () => [],
})

const emit = defineEmits<{
  'update:modelValue': [v: ModelValue]
}>()

const { t } = useI18n()
const themeVars = useThemeVars()
const isOpen = ref(false)
const isDatePickerOpen = ref(false)
const filterName = computed(() => props.name || t('canvasUI.components.filters.dateRangeLabel'))
const tagStyle = computed(() => ({
  '--n-color': activeFilter.value || props.modelValue ? themeVars.value.primary5 : themeVars.value.primary6,
  '--n-border': `1px solid ${themeVars.value.primary5}`,
  '--n-height': '28px',
}))

const activeFilter = computed(() =>
  props.options.find((o) => isArrayEqual(props.modelValue, translatePredefinedRange(o.value))),
)

function isArrayEqual(array1: ModelValue, array2: ModelValue): boolean {
  return !!(array1 && array2 && array1[0] === array2[0] && array1[1] === array2[1])
}

function translatePredefinedRange(value?: Option['value']): ModelValue {
  const today = new Date()
  switch (value) {
    case 'last_week':
      return [format(subDays(today, 7), METADATA_FILTER_DATE_FORMAT), format(today, METADATA_FILTER_DATE_FORMAT)]
    case 'last_month':
      return [format(subDays(today, 30), METADATA_FILTER_DATE_FORMAT), format(today, METADATA_FILTER_DATE_FORMAT)]
    case 'last_year':
      return [format(subYears(today, 1), METADATA_FILTER_DATE_FORMAT), format(today, METADATA_FILTER_DATE_FORMAT)]
    default:
      return null
  }
}

function onOptionSelect(option: Option, selected: boolean) {
  if (selected) {
    const range = translatePredefinedRange(option.value)
    emit('update:modelValue', range)
  } else {
    emit('update:modelValue', null)
  }
}

function onCustomRangeUpdate(_: [number, number] | null, value: ModelValue) {
  const start = value?.[0]
  const end = value?.[1]
  start && end && emit('update:modelValue', [start, end])
}

function onOutsideClick() {
  isOpen.value = false
}

function onClearSelection() {
  emit('update:modelValue', null)
  isOpen.value = false
}

function togglePopover() {
  isOpen.value = !isOpen.value
}
</script>

<style scoped lang="scss">
.c-date-range-filter__popover {
  box-shadow: 0px 7px 24px 0px rgba(0, 0, 0, 0.1);
}

.c-date-range-filter--inline :deep(.n-date-panel-actions__suffix) {
  display: none;
}
</style>
